import {
  canReplacePilot,
  characterCount,
  matchesPilotFilter,
  PILOT_FILTERS,
  pilotReserveFor,
  PilotSwapReasonSchema,
  splitPilotCandidates,
  summarizePilot,
  type PilotCandidate,
  type PilotFilter,
  type PilotProgress,
  type PilotRow,
  type TeamRole,
} from '@antoja/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Fragment, useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { t } from '../i18n';
import { Icon } from '../icons';
import type { ConsoleApi } from '../lib/api';
import { errorText } from '../lib/errors';
import { formatDate, statusLabel } from '../places/format';
import { controlClass, selectBase, surfaceClass } from '../styles';
import { Button, Field, Notice, PageHeader, SegmentedControl } from '../ui';
import { formatDistance, formatSimilarity, linkStateIcons, roleLabel, sourceLabel } from './format';

// Toolbar controls share one height: the segmented control is 44 px buttons in a 4 px frame.
const toolbarControl = `${selectBase} min-[640px]:min-h-[52px]`;

/** The pilot list: link each place to Foursquare, and (admins) replace what is not there. */
export function PilotScreen({ api, role }: { api: ConsoleApi; role: TeamRole }) {
  const canEdit = role !== 'verifier';
  const list = useQuery({ queryKey: ['pilot'], queryFn: () => api.listPilot() });
  const [filter, setFilter] = useState<PilotFilter>('all');
  const [zoneId, setZoneId] = useState<number | null>(null);
  const [openKey, setOpenKey] = useState<string | null>(null);
  // A row an action moved out of the filter stays in view until the filter changes.
  const [movedKey, setMovedKey] = useState<string | null>(null);
  const [announcement, setAnnouncement] = useState('');
  const toggles = useRef(new Map<string, HTMLButtonElement>());

  const rows = list.data ?? [];
  const summary = summarizePilot(rows);
  const inZone = rows.filter((row) => zoneId === null || row.zoneId === zoneId);
  const shown = inZone.filter((row) => matchesPilotFilter(row, filter) || row.listKey === movedKey);
  const countOf = (option: PilotFilter) =>
    inZone.filter((row) => matchesPilotFilter(row, option)).length;

  // Choosing another filter or zone by hand closes the open row.
  function changeFilter(next: PilotFilter) {
    setFilter(next);
    setOpenKey(null);
    setMovedKey(null);
  }
  function changeZone(next: number | null) {
    setZoneId(next);
    setOpenKey(null);
    setMovedKey(null);
  }

  // After a change, focus goes back to the row's button and the outcome is announced.
  function done(row: PilotRow, message: string) {
    setAnnouncement(message);
    setMovedKey(row.listKey);
    toggles.current.get(row.listKey)?.focus();
  }

  return (
    <>
      <PageHeader title={t('pilot.title')} subtitle={t('pilot.subtitle')} />
      <p role="status" className="sr-only">
        {announcement}
      </p>

      {list.isPending ? (
        <p role="status" className="text-muted">
          {t('pilot.loading')}
        </p>
      ) : null}
      {list.isError ? (
        <div role="alert" className={`grid justify-items-start gap-3 p-5 ${surfaceClass}`}>
          <p>{t('pilot.error')}</p>
          <Button onClick={() => void list.refetch()}>{t('common.retry')}</Button>
        </div>
      ) : null}
      {list.isSuccess && rows.length === 0 ? (
        <p className={`p-5 text-muted ${surfaceClass}`}>{t('pilot.empty')}</p>
      ) : null}

      {list.isSuccess && rows.length > 0 ? (
        <>
          <section aria-labelledby="pilot-summary" className="grid gap-3">
            <h2 id="pilot-summary" className="sr-only">
              {t('pilot.summary')}
            </h2>
            <ul className="grid grid-cols-[repeat(auto-fit,minmax(min(220px,100%),1fr))] gap-3">
              <SummaryTile label={t('pilot.allZones')} progress={summary.total} />
              {summary.zones.map((zone) => (
                <SummaryTile key={zone.zoneId} label={zone.zoneName} progress={zone} />
              ))}
            </ul>
          </section>

          {!canEdit ? <p className="text-sm text-muted">{t('pilot.readOnly')}</p> : null}

          <div className="flex flex-wrap items-end justify-between gap-3 max-[639px]:grid max-[639px]:grid-cols-1">
            <div className="max-[639px]:hidden">
              <SegmentedControl
                label={t('pilot.filterLabel')}
                options={PILOT_FILTERS}
                value={filter}
                optionLabel={(option) => t(`pilot.filter.${option}`)}
                count={countOf}
                countUnit={t('pilot.countUnit')}
                onChange={changeFilter}
              />
            </div>
            {/* Phones: a select instead of three rows of segments. */}
            <div className="grid gap-1.5 min-[640px]:hidden">
              <label htmlFor="pilot-filter" className="text-sm font-medium">
                {t('pilot.filterLabel')}
              </label>
              <select
                id="pilot-filter"
                value={filter}
                onChange={(event) => changeFilter(event.target.value as PilotFilter)}
                className={`${selectBase} w-full`}
              >
                {PILOT_FILTERS.map((option) => (
                  <option key={option} value={option}>
                    {t('pilot.filterOption', {
                      label: t(`pilot.filter.${option}`),
                      count: countOf(option),
                    })}
                  </option>
                ))}
              </select>
            </div>
            <div className="ml-auto flex items-center gap-2 max-[639px]:ml-0 max-[639px]:grid max-[639px]:gap-1.5">
              <label
                htmlFor="pilot-zone"
                className="text-sm text-muted max-[639px]:font-medium max-[639px]:text-ink"
              >
                {t('places.zone')}
              </label>
              <select
                id="pilot-zone"
                value={zoneId ?? ''}
                onChange={(event) =>
                  changeZone(event.target.value ? Number(event.target.value) : null)
                }
                className={`${toolbarControl} max-[639px]:w-full`}
              >
                <option value="">{t('places.allZones')}</option>
                {summary.zones.map((zone) => (
                  <option key={zone.zoneId} value={zone.zoneId}>
                    {zone.zoneName}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {shown.length === 0 ? (
            <p className={`p-5 text-muted ${surfaceClass}`}>{t('pilot.filteredEmpty')}</p>
          ) : (
            <div
              role="region"
              aria-labelledby="pilot-caption"
              tabIndex={0}
              className={`overflow-x-auto ${surfaceClass}`}
            >
              <table
                role="table"
                className="w-full border-collapse text-left text-[14.5px] max-[639px]:block min-[640px]:min-w-[900px] min-[640px]:table-fixed"
              >
                <caption id="pilot-caption" className="sr-only">
                  {t('pilot.title')}
                </caption>
                {/* Fixed widths keep the columns still; Lugar and its FSQ place share the rest. */}
                <colgroup>
                  <col />
                  <col className="w-[120px]" />
                  <col className="w-[148px]" />
                  <col className="w-[204px]" />
                  <col />
                  <col className="w-[220px]" />
                </colgroup>
                <thead role="rowgroup" className="text-[13px] text-muted max-[639px]:sr-only">
                  <tr role="row">
                    {(['name', 'zone', 'role', 'link', 'fsq'] as const).map((column) => (
                      <th
                        key={column}
                        role="columnheader"
                        scope="col"
                        className="border-b border-line px-4 py-3 font-semibold whitespace-nowrap"
                      >
                        {t(`pilot.column.${column}`)}
                      </th>
                    ))}
                    <th role="columnheader" scope="col" className="border-b border-line px-4 py-3">
                      <span className="sr-only">{t('pilot.column.actions')}</span>
                    </th>
                  </tr>
                </thead>
                <tbody role="rowgroup" className="max-[639px]:block">
                  {shown.map((row) => {
                    const open = openKey === row.listKey;
                    const panelId = `pilot-panel-${row.listKey.replace(/\W/g, '-')}`;
                    const outOfFilter = !matchesPilotFilter(row, filter);
                    return (
                      <Fragment key={row.listKey}>
                        <tr
                          role="row"
                          className={`max-[639px]:grid max-[639px]:grid-cols-2 max-[639px]:gap-x-4 max-[639px]:gap-y-3 max-[639px]:p-5 ${
                            open ? 'bg-canvas' : 'border-b border-line'
                          }`}
                        >
                          <th
                            role="rowheader"
                            scope="row"
                            className="px-4 py-3.5 align-middle font-semibold text-balance max-[639px]:col-span-2 max-[639px]:p-0"
                          >
                            {row.name}
                            {outOfFilter ? (
                              <span className="mt-0.5 block text-[13px] font-normal text-muted">
                                {t('pilot.notInFilter')}
                              </span>
                            ) : null}
                          </th>
                          <Cell label={t('pilot.column.zone')}>{row.zoneName}</Cell>
                          <Cell label={t('pilot.column.role')}>{roleLabel(row)}</Cell>
                          <Cell label={t('pilot.column.link')}>
                            <span className="inline-flex items-center gap-1.5">
                              <Icon name={linkStateIcons[row.linkState]} size={16} />
                              {t(`pilot.state.${row.linkState}`)}
                            </span>
                          </Cell>
                          <Cell label={t('pilot.column.fsq')}>
                            {row.placeName ?? (
                              <span className="text-muted">{t('pilot.noFsqPlace')}</span>
                            )}
                          </Cell>
                          <td
                            role="cell"
                            className="px-4 py-2 text-right align-middle max-[639px]:col-span-2 max-[639px]:p-0"
                          >
                            <Button
                              ref={(element) => {
                                if (element) toggles.current.set(row.listKey, element);
                                else toggles.current.delete(row.listKey);
                              }}
                              aria-expanded={open}
                              aria-controls={open ? panelId : undefined}
                              aria-label={t(
                                row.linkState === 'linked'
                                  ? 'pilot.open.linkedLabel'
                                  : 'pilot.open.findLabel',
                                { name: row.name },
                              )}
                              onClick={() => setOpenKey(open ? null : row.listKey)}
                              className="max-[639px]:w-full min-[640px]:min-w-[188px]"
                            >
                              {t(
                                row.linkState === 'linked'
                                  ? 'pilot.open.linked'
                                  : 'pilot.open.find',
                              )}
                            </Button>
                          </td>
                        </tr>
                        {open ? (
                          <tr
                            role="row"
                            className="border-b border-line bg-canvas max-[639px]:block max-[639px]:px-5 max-[639px]:pb-5"
                          >
                            <td
                              role="cell"
                              colSpan={6}
                              className="px-4 pb-4 max-[639px]:block max-[639px]:p-0"
                            >
                              <PilotPanel
                                id={panelId}
                                api={api}
                                row={row}
                                rows={rows}
                                canEdit={canEdit}
                                isAdmin={role === 'admin'}
                                onDone={(message) => done(row, message)}
                              />
                            </td>
                          </tr>
                        ) : null}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      ) : null}
    </>
  );
}

/** "40 de 60" big, then "vinculados · meta 54", a bar, and the reserve. */
function SummaryTile({ label, progress }: { label: string; progress: PilotProgress }) {
  const share = progress.selected === 0 ? 0 : progress.linked / progress.selected;
  const reached = progress.selected > 0 && progress.linked >= progress.goal;
  return (
    <li className={`grid content-start gap-2.5 p-5 ${surfaceClass}`}>
      <span className="text-sm font-medium text-muted">{label}</span>
      <span className="text-[34px] leading-none font-semibold tracking-[-0.03em] tabular-nums">
        {t('pilot.progress', { linked: progress.linked, selected: progress.selected })}
      </span>
      {progress.selected === 0 ? (
        <span className="text-sm text-muted">{t('pilot.noSelected')}</span>
      ) : (
        <span className="flex flex-wrap items-center gap-x-1.5 text-sm">
          <span>{t('pilot.linkedWord')}</span>
          <span aria-hidden="true">·</span>
          <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
            {reached ? <Icon name="check" size={16} /> : null}
            {t(reached ? 'pilot.goalReached' : 'pilot.goal', { goal: progress.goal })}
          </span>
        </span>
      )}
      <span aria-hidden="true" className="relative h-2 overflow-hidden rounded-full bg-canvas">
        <span
          className="absolute inset-y-0 left-0 rounded-full bg-ink"
          style={{ width: `${Math.round(share * 100)}%` }}
        />
      </span>
      <span className="text-[13px] whitespace-nowrap text-muted tabular-nums">
        {progress.reserve === 0
          ? t('pilot.reserveNone')
          : t('pilot.reserveProgress', { linked: progress.reserveLinked, total: progress.reserve })}
      </span>
    </li>
  );
}

/** A table cell; on phones each one shows its column name above the value. */
function Cell({ label, children }: { label: string; children: ReactNode }) {
  return (
    <td role="cell" className="px-4 py-3.5 align-middle max-[639px]:p-0">
      <span aria-hidden="true" className="mb-1 block text-[13px] text-muted min-[640px]:hidden">
        {label}
      </span>
      {children}
    </td>
  );
}

type PanelAction =
  { kind: 'link'; placeId: string; name: string } | { kind: 'unlink' } | { kind: 'notInFsq' };

/** What can be done with one row: its candidates, its link, or its replacement. */
function PilotPanel({
  id,
  api,
  row,
  rows,
  canEdit,
  isAdmin,
  onDone,
}: {
  id: string;
  api: ConsoleApi;
  row: PilotRow;
  rows: readonly PilotRow[];
  canEdit: boolean;
  isAdmin: boolean;
  onDone: (message: string) => void;
}) {
  const queryClient = useQueryClient();
  const [swapping, setSwapping] = useState(false);
  const [showOthers, setShowOthers] = useState(false);
  const errorRef = useRef<HTMLParagraphElement>(null);
  const linked = row.linkState === 'linked';
  // A zone without any reserve row has nothing to offer: no "Reemplazar" button at all.
  const hasReserve = rows.some((other) => other.role === 'reserve' && other.zoneId === row.zoneId);
  const candidates = useQuery({
    queryKey: ['pilot-candidates', row.listKey],
    queryFn: () => api.listPilotCandidates(row.listKey),
    enabled: !linked,
  });
  const change = useMutation({
    mutationFn: (action: PanelAction) =>
      action.kind === 'link'
        ? api.linkPilotPlace(row.listKey, action.placeId)
        : action.kind === 'unlink'
          ? api.unlinkPilotPlace(row.listKey)
          : api.markPilotNotInFsq(row.listKey),
    onSuccess: async (_data, action) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['pilot'] }),
        queryClient.invalidateQueries({ queryKey: ['pilot-candidates', row.listKey] }),
      ]);
      onDone(
        action.kind === 'link'
          ? t('pilot.done.linked', { name: row.name, place: action.name })
          : t(action.kind === 'unlink' ? 'pilot.done.unlinked' : 'pilot.done.notInFsq', {
              name: row.name,
            }),
      );
    },
  });
  // A refused change: focus moves to the reason, so it is read and not lost.
  useEffect(() => {
    if (change.isError) errorRef.current?.focus();
  }, [change.isError]);

  // Buttons stay focusable while a change runs (aria-disabled), so focus is never dropped.
  const busy = change.isPending;
  const act = (action: PanelAction) => {
    if (!busy) change.mutate(action);
  };
  const split = candidates.isSuccess ? splitPilotCandidates(candidates.data) : null;

  return (
    <div
      id={id}
      className={`grid gap-4 p-5 ${surfaceClass} max-[639px]:rounded-none max-[639px]:border-0 max-[639px]:bg-transparent max-[639px]:p-0`}
    >
      {linked ? (
        <div className="grid gap-1">
          <p className="font-semibold">
            {t('pilot.linkedTo', { place: row.placeName ?? row.name })}
          </p>
          {row.fsqId ? (
            <p className="text-[13px] break-all text-muted">
              {t('pilot.fsqId', { id: row.fsqId })}
            </p>
          ) : null}
          {row.status && row.statusAt ? (
            <p className="text-[13px] text-muted">
              {t('pilot.statusLine', {
                status: statusLabel(row.status),
                source: sourceLabel(row.statusSource),
                date: formatDate(row.statusAt),
              })}
            </p>
          ) : null}
        </div>
      ) : (
        <div className="grid gap-3">
          <div className="grid gap-1">
            <h3 className="text-[15px] font-semibold">
              {t('pilot.candidates.title', { zone: row.zoneName })}
            </h3>
            <p className="text-[13px] text-muted">{t('pilot.candidates.help')}</p>
            {row.linkState === 'doubtful' ? (
              <p className="inline-flex items-center gap-1.5 text-sm font-medium">
                <Icon name="conflict" size={16} />
                {t('pilot.doubtfulHint')}
              </p>
            ) : null}
            {row.linkState === 'not_in_fsq' ? (
              <p className="inline-flex items-center gap-1.5 text-sm font-medium">
                <Icon name="cross" size={16} />
                {t('pilot.markedNotInFsq')}
              </p>
            ) : null}
          </div>
          {candidates.isPending ? (
            <p role="status" className="text-sm text-muted">
              {t('pilot.candidates.loading')}
            </p>
          ) : null}
          {candidates.isError ? (
            <div role="alert" className="grid justify-items-start gap-2 text-sm">
              <p>{t('pilot.candidates.error')}</p>
              <Button onClick={() => void candidates.refetch()}>{t('common.retry')}</Button>
            </div>
          ) : null}
          {split && split.likely.length === 0 && split.unlikely.length === 0 ? (
            <p className="text-sm text-muted">
              {t('pilot.candidates.empty', { zone: row.zoneName })}
            </p>
          ) : null}
          {split && split.likely.length === 0 && split.unlikely.length > 0 ? (
            <p className="text-sm text-muted">{t('pilot.candidates.noneLikely')}</p>
          ) : null}
          {split && split.likely.length > 0 ? (
            <CandidateList
              candidates={split.likely}
              canEdit={canEdit}
              busy={busy}
              onLink={(candidate) =>
                act({ kind: 'link', placeId: candidate.placeId, name: candidate.name })
              }
            />
          ) : null}
          {split && split.unlikely.length > 0 ? (
            <div className="grid justify-items-start gap-3">
              <Button
                aria-expanded={showOthers}
                aria-controls={showOthers ? `${id}-others` : undefined}
                onClick={() => setShowOthers((value) => !value)}
              >
                {t('pilot.candidates.others', { zone: row.zoneName })}
              </Button>
              {showOthers ? (
                <div id={`${id}-others`} className="w-full">
                  <CandidateList
                    candidates={split.unlikely}
                    canEdit={canEdit}
                    busy={busy}
                    unlikely
                    onLink={(candidate) =>
                      act({ kind: 'link', placeId: candidate.placeId, name: candidate.name })
                    }
                  />
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      )}

      {change.isError ? <Notice ref={errorRef}>{errorText(change.error)}</Notice> : null}

      {canEdit &&
      (linked ||
        row.linkState !== 'not_in_fsq' ||
        (isAdmin && canReplacePilot(row) && hasReserve)) ? (
        <div className="flex flex-wrap gap-2 border-t border-line pt-4 max-[639px]:grid">
          {linked ? (
            <Button aria-disabled={busy} onClick={() => act({ kind: 'unlink' })}>
              {t('pilot.unlink')}
            </Button>
          ) : null}
          {!linked && row.linkState !== 'not_in_fsq' ? (
            <Button aria-disabled={busy} onClick={() => act({ kind: 'notInFsq' })}>
              {t('pilot.notInFsq')}
            </Button>
          ) : null}
          {isAdmin && canReplacePilot(row) && hasReserve ? (
            <Button
              aria-expanded={swapping}
              aria-controls={swapping ? `${id}-swap` : undefined}
              onClick={() => setSwapping((value) => !value)}
            >
              {t('pilot.swap.open')}
            </Button>
          ) : null}
        </div>
      ) : null}

      {isAdmin && canReplacePilot(row) && !hasReserve ? (
        <p className="border-t border-line pt-4 text-sm text-muted">
          {t('pilot.swap.noReserve', { zone: row.zoneName })}
        </p>
      ) : null}

      {isAdmin && swapping && canReplacePilot(row) && hasReserve ? (
        <SwapForm
          id={`${id}-swap`}
          api={api}
          row={row}
          reserve={pilotReserveFor(rows, row)}
          onCancel={() => setSwapping(false)}
          onDone={onDone}
        />
      ) : null}
    </div>
  );
}

/** FSQ places that look like the row, each with its similarity, distance and "Vincular". */
function CandidateList({
  candidates,
  canEdit,
  busy,
  unlikely = false,
  onLink,
}: {
  candidates: readonly PilotCandidate[];
  canEdit: boolean;
  busy: boolean;
  unlikely?: boolean;
  onLink: (candidate: PilotCandidate) => void;
}) {
  return (
    <ul className="grid divide-y divide-line rounded-xl border border-line bg-surface">
      {candidates.map((candidate) => {
        const takenId = `taken-${candidate.placeId}`;
        const taken = candidate.takenByListKey !== null;
        return (
          <li
            key={candidate.placeId}
            className="grid items-center gap-x-4 gap-y-2 px-4 py-3 min-[640px]:grid-cols-[minmax(0,1fr)_auto_auto]"
          >
            <span className="grid min-w-0 gap-0.5">
              <span className="font-semibold">{candidate.name}</span>
              {candidate.address ? (
                <span className="text-[13px] text-muted">{candidate.address}</span>
              ) : null}
              {taken ? (
                <span id={takenId} className="text-[13px] font-medium">
                  {t('pilot.takenBy', { name: candidate.takenByName ?? t('pilot.anotherRow') })}
                </span>
              ) : null}
            </span>
            <span className="grid gap-0.5 text-[13px] text-muted tabular-nums min-[640px]:text-right">
              <span>
                {unlikely ? `${t('pilot.lowSimilarity')} · ` : null}
                {t('pilot.similarity', { value: formatSimilarity(candidate.similarity) })}
              </span>
              {candidate.distanceM !== null ? (
                <span>{t('pilot.distance', { value: formatDistance(candidate.distanceM) })}</span>
              ) : null}
            </span>
            {canEdit ? (
              <Button
                aria-disabled={busy || taken}
                aria-describedby={taken ? takenId : undefined}
                aria-label={t('pilot.linkLabel', { name: candidate.name })}
                onClick={() => {
                  if (!taken) onLink(candidate);
                }}
                className="max-[639px]:w-full"
              >
                {t('pilot.link')}
              </Button>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}

/** Admins: pick a linked reserve place of the same zone, give a reason, then confirm. */
function SwapForm({
  id,
  api,
  row,
  reserve,
  onCancel,
  onDone,
}: {
  id: string;
  api: ConsoleApi;
  row: PilotRow;
  reserve: readonly PilotRow[];
  onCancel: () => void;
  onDone: (message: string) => void;
}) {
  const queryClient = useQueryClient();
  const [choice, setChoice] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [problem, setProblem] = useState<'choice' | 'reason' | null>(null);
  const confirmRef = useRef<HTMLButtonElement>(null);
  const swapErrorRef = useRef<HTMLParagraphElement>(null);
  const firstRadioRef = useRef<HTMLInputElement>(null);
  const reasonRef = useRef<HTMLInputElement>(null);
  const chosen = reserve.find((candidate) => candidate.listKey === choice) ?? null;
  const reasonLength = characterCount(reason.trim());
  const reasonOk = PilotSwapReasonSchema.safeParse(reason).success;
  const swap = useMutation({
    mutationFn: () => api.swapPilotPlace(row.listKey, choice ?? '', reason),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['pilot'] });
      onDone(t('pilot.done.swapped', { name: row.name, reserve: chosen?.name ?? '' }));
    },
  });

  // Focus goes to the first thing to fix, then to "yes" once there is nothing left.
  function review(event: FormEvent) {
    event.preventDefault();
    const next = chosen === null ? 'choice' : !reasonOk ? 'reason' : null;
    setProblem(next);
    if (next === 'choice') firstRadioRef.current?.focus();
    if (next === 'reason') reasonRef.current?.focus();
    if (next === null) setConfirming(true);
  }
  useEffect(() => {
    if (confirming) confirmRef.current?.focus();
  }, [confirming]);
  useEffect(() => {
    if (swap.isError) swapErrorRef.current?.focus();
  }, [swap.isError]);

  if (reserve.length === 0) {
    return (
      <p id={id} className="text-sm text-muted">
        {t('pilot.swap.none', { zone: row.zoneName })}
      </p>
    );
  }

  return (
    <form id={id} className="grid gap-4 border-t border-line pt-4" onSubmit={review} noValidate>
      <fieldset
        className="grid gap-2"
        disabled={confirming || swap.isPending}
        aria-describedby={problem === 'choice' ? `${id}-problem` : undefined}
      >
        <legend className="mb-2 text-sm font-medium">
          {t('pilot.swap.legend', { zone: row.zoneName })}
        </legend>
        {reserve.map((candidate, index) => (
          <label
            key={candidate.listKey}
            className={`flex min-h-11 cursor-pointer items-center gap-3 rounded-xl border bg-surface px-3.5 py-2 ${
              choice === candidate.listKey ? 'border-ink' : 'border-line'
            }`}
          >
            <input
              ref={index === 0 ? firstRadioRef : undefined}
              type="radio"
              name={`${id}-reserve`}
              value={candidate.listKey}
              checked={choice === candidate.listKey}
              onChange={() => {
                setChoice(candidate.listKey);
                if (problem === 'choice') setProblem(null);
              }}
              aria-invalid={problem === 'choice'}
              className="size-4 accent-ink"
            />
            <span className="grid">
              <span className="font-medium">{candidate.name}</span>
              <span className="text-[13px] text-muted">
                {candidate.placeName && candidate.placeName !== candidate.name
                  ? `${roleLabel(candidate)} · ${candidate.placeName}`
                  : roleLabel(candidate)}
              </span>
            </span>
          </label>
        ))}
        <Field
          label={t('pilot.swap.reason')}
          htmlFor={`${id}-reason`}
          hint={t('pilot.swap.reasonHint', { count: reasonLength })}
          hintId={`${id}-reason-hint`}
          className="mt-2"
        >
          <input
            ref={reasonRef}
            id={`${id}-reason`}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            aria-invalid={problem === 'reason'}
            aria-describedby={
              problem === 'reason' ? `${id}-reason-hint ${id}-problem` : `${id}-reason-hint`
            }
            className={controlClass}
          />
        </Field>
      </fieldset>
      {problem ? (
        <Notice id={`${id}-problem`}>
          {t(problem === 'choice' ? 'pilot.swap.noChoice' : 'pilot.swap.badReason')}
        </Notice>
      ) : null}
      {swap.isError ? <Notice ref={swapErrorRef}>{errorText(swap.error)}</Notice> : null}
      {confirming && chosen ? (
        <div className="grid gap-3 rounded-xl bg-canvas p-5 max-[639px]:p-4">
          <p className="text-sm">
            {t('pilot.swap.confirm', { name: row.name, reserve: chosen.name })}
          </p>
          <div className="flex flex-wrap gap-2 max-[639px]:grid">
            <Button
              ref={confirmRef}
              variant="primary"
              aria-disabled={swap.isPending}
              onClick={() => {
                if (!swap.isPending) swap.mutate();
              }}
            >
              {t('pilot.swap.yes')}
            </Button>
            <Button onClick={() => setConfirming(false)}>{t('common.cancel')}</Button>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap gap-2 max-[639px]:grid">
          <Button type="submit">{t('pilot.swap.review')}</Button>
          <Button onClick={onCancel}>{t('common.cancel')}</Button>
        </div>
      )}
    </form>
  );
}
